/**
 * Rolling 24-hour scoreboard for diamonds — SIMULATED, not traded.
 *
 * Every diamond the engine judged in the window, and what each would have done
 * against a fixed +6 / -105 bracket. It says whether the signals were RIGHT,
 * not whether you would have made money on them.
 *
 * Four position models, because they answer different questions — see
 * `runPositionModel` for what each one does. The short version:
 *
 *   ALL    every signal on its own          "how good is the rule?"
 *   1 POS  the live bot rule                "what could I actually take?"
 *   FLIP   reverse on an opposite signal    "what does sitting out cost?"
 *   MART   reverse, doubling after a loss   "what does doubling cost?"
 *
 * The gap between ALL and 1 POS is the honest price of the bot's one-position-
 * per-account rule, and it is usually large — a 6/105 bracket holds losers for
 * a very long time, and every signal during that hold is lost.
 *
 * What is still NOT modelled in any mode: blackout windows, cooldowns, ATR
 * gates, and commission. Commission is the big one at these trade counts — the
 * reversing modes book two to three times as many round turns, which at a
 * realistic rate is enough to flip their apparent advantage. Treat every figure
 * as an upper bound, not a forecast.
 *
 * Aggregated on the client from the `simulated` block the engine attaches to
 * each diamond. No new endpoint, and no second implementation of the walk that
 * decides a win from a loss.
 */

import React, { useMemo, useState } from 'react';
import { useCandidateStore } from '../../store/candidateStore';
import { useSessionStart } from '../../hooks/useSessionStart';
import type { EngineDiamond } from '../../utils/setupCandidates';
import {
  dollarsPerPoint,
  lowWaterMark,
  maxDrawdown,
  runPositionModel,
  POSITION_MODELS,
  type PositionModel,
} from '../../utils/diamondPositionModels';
import './DiamondStatsOverlay.css';

const WINDOW_MS = 24 * 60 * 60 * 1000;

const COLLAPSE_KEY = 'dso:collapsed';
const MODEL_KEY = 'dso:model';
const WINDOW_KEY = 'dso:window';

/** Read a persisted choice, falling back when storage is unavailable. */
function persisted<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

function remember(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Preference just does not persist; the control still works.
  }
}

/** Rolling 24h, or everything since the session opened at 5:02pm CT. */
type StatsWindow = '24h' | 'session';

/** Short enough for the overlay strip; `title` carries the real explanation. */
const MODEL_LABEL: Record<PositionModel, string> = {
  all: 'all',
  skip: '1 pos',
  flip: 'flip',
  martingale: 'mart',
};

const MODEL_HELP: Record<PositionModel, string> = {
  all: 'Every signal judged on its own, ignoring whether you were already positioned',
  skip: 'The live bot rule: skip anything that arrives while a trade is open',
  flip: 'An opposite signal closes the position and reverses at the same size',
  martingale: 'As flip, but size doubles after each losing close and resets on a win',
};

interface Tally {
  wins: number;
  losses: number;
  points: number;
  dollars: number;
}

const EMPTY: Tally = { wins: 0, losses: 0, points: 0, dollars: 0 };

export const DiamondStatsOverlay: React.FC = () => {
  const engineDiamonds = useCandidateStore((s) => s.engineDiamonds);
  // The bot's floor and the grade cutoffs, so the table can say which rows the
  // bot would actually act on rather than leaving it to be inferred.
  const botFloor = useCandidateStore((s) => s.botMinConfidence);
  const thresholds = useCandidateStore((s) => s.thresholds);
  // Defaults: the reversing book over the current session, because that is the
  // pair being watched day to day. Both persist, so switching away sticks.
  const [model, setModel] = useState<PositionModel>(() =>
    persisted(MODEL_KEY, POSITION_MODELS, 'martingale')
  );
  /**
   * Collapsed to a single summary line.
   *
   * Persisted because it is a layout preference, not session state: a card
   * deliberately shrunk to stay out of the chart's way must not spring back
   * open on every reload. localStorage rather than the Library store — that
   * store answers "is this overlay on", which is a different question, and
   * hiding the card is already its answer.
   */
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      // Private mode or storage disabled. Defaulting to expanded shows more
      // rather than silently hiding the figures.
      return false;
    }
  });

  const toggleCollapsed = () =>
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        // Preference just does not persist; the toggle still works.
      }
      return next;
    });
  const [window_, setWindow] = useState<StatsWindow>(() =>
    persisted(WINDOW_KEY, ['24h', 'session'] as const, 'session')
  );
  // Fetched, not computed: 5:02pm CT with DST and holidays is owned by the
  // engine, and a second implementation here is the duplication this repo bans.
  const session = useSessionStart();

  const { opportunities, outcome, tally, byGrade, drawdown, lowWater, openCount } = useMemo(() => {
    // Fall back to the rolling window until the session boundary arrives,
    // rather than scoping to a boundary we would be guessing at.
    const cutoff =
      window_ === 'session' && session
        ? session.sessionStartMs
        : Date.now() - WINDOW_MS;
    const inWindow = Object.values(engineDiamonds)
      .filter((d): d is EngineDiamond => !!d && d.timestamp >= cutoff && !!d.simulated)
      .sort((a, b) => a.timestamp - b.timestamp);

    const perPoint = dollarsPerPoint(inWindow);
    const result = runPositionModel(inWindow, model, perPoint);

    const t: Tally = { ...EMPTY };
    const grades = new Map<string, Tally>();
    for (const trade of result.trades) {
      const g = grades.get(trade.grade) ?? { ...EMPTY };
      for (const x of [t, g]) {
        if (trade.won) x.wins++;
        else x.losses++;
        x.points += trade.points;
        x.dollars += trade.dollars;
      }
      grades.set(trade.grade, g);
    }

    const ORDER = ['super-strong', 'strong', 'above-moderate', 'moderate', 'weak'];
    const sortedGrades = [...grades.entries()].sort(
      (a, b) => ORDER.indexOf(a[0]) - ORDER.indexOf(b[0])
    );

    return {
      opportunities: inWindow.length,
      outcome: result,
      tally: t,
      byGrade: sortedGrades,
      drawdown: maxDrawdown(result.trades.map((x) => x.dollars)),
      // How far under the starting line it actually went, which is a different
      // question from how much it gave back off a high.
      lowWater: lowWaterMark(result.trades.map((x) => x.dollars)),
      // Signals that entered but never resolved. Counted here rather than in
      // the model, which only reports things it could actually book.
      openCount: inWindow.filter((d) => d.simulated?.result === 'open').length,
    };
  }, [engineDiamonds, model, window_, session]);

  /**
   * The score range a grade covers, as [floor, ceiling).
   *
   * Weak has no floor of its own — it is everything under `moderate` — so it
   * starts at zero, and super-strong has no ceiling.
   */
  const gradeRange = (grade: string): [number, number] | null => {
    if (!thresholds) return null;
    const { moderateScore, aboveModerateScore, strongScore, superStrongScore } = thresholds;
    switch (grade) {
      case 'super-strong':
        return [superStrongScore, Infinity];
      case 'strong':
        return [strongScore, superStrongScore];
      case 'above-moderate':
        return [aboveModerateScore, strongScore];
      case 'moderate':
        return [moderateScore, aboveModerateScore];
      case 'weak':
        return [0, moderateScore];
      default:
        return null;
    }
  };

  /**
   * How much of a grade the bot's floor lets through.
   *
   * THREE states, not two. A grade that straddles the floor is partly traded,
   * and calling it either way is wrong in a way that matters: marking it taken
   * overstates what the bot does, and striking it out implies none of it
   * trades when most of it may. Weak at 0-40 against a floor of 25 is exactly
   * that case — the majority of its range IS traded.
   */
  type Coverage = 'all' | 'partial' | 'none' | null;

  const coverage = (grade: string): Coverage => {
    if (botFloor === null || botFloor === undefined) return null;
    const range = gradeRange(grade);
    if (!range) return null;
    const [floor, ceil] = range;
    if (floor >= botFloor) return 'all';
    if (ceil <= botFloor) return 'none';
    return 'partial';
  };

  const sessionApplied = window_ === 'session' && !!session;

  const decided = tally.wins + tally.losses;
  const winRate = decided > 0 ? (100 * tally.wins) / decided : null;
  const reversing = model === 'flip' || model === 'martingale';

  return (
    <div className="dso">
      <div className="dso-head">
        {/* The title IS the control. A separate chevron would be another
            8px target on a card whose whole problem is that it is too big. */}
        <button
          className="dso-title dso-title-btn"
          onClick={toggleCollapsed}
          title={collapsed ? 'Expand' : 'Collapse to one line'}
          aria-expanded={!collapsed}
        >
          {collapsed ? '▸' : '▾'} DIAMONDS
        </button>
        {/* The window is part of what the number MEANS, so it stays in the
            title rather than hiding in a menu. Disabled until the engine has
            answered, because an unavailable boundary silently falling back to
            24h would be a lie in the label. */}
        <button
          className="dso-window"
          onClick={() =>
            setWindow((w) => {
              const next = w === '24h' ? 'session' : '24h';
              remember(WINDOW_KEY, next);
              return next;
            })
          }
          disabled={!session}
          title={
            session
              ? 'Toggle between a rolling 24 hours and this trading session (from 5:02pm CT)'
              : 'Waiting for the engine to report the session boundary'
          }
        >
          {/* `session` is the stored preference; `sessionApplied` is whether
              the engine has actually given us the boundary. Until it has, the
              cutoff falls back to the rolling window, and labelling that
              SESSION would state a window the figures do not cover. */}
          {sessionApplied ? 'SESSION' : '24H'}
        </button>
        <span className="dso-sim" title="Simulated against a fixed 6/105 bracket. Not traded.">
          sim
        </span>
      </div>

      {collapsed ? (
        opportunities === 0 ? (
          <div className="dso-empty">
            {window_ === 'session' ? 'no diamonds this session' : 'no diamonds in the last 24h'}
          </div>
        ) : (
          // The four numbers worth keeping when there is no room: how many,
          // how they split, the rate, and what it made.
          <div className="dso-summary" onClick={toggleCollapsed} title="Expand">
            <span className="dso-opps">{opportunities}</span>
            <span className="dso-wl">
              <b className="win">{tally.wins}W</b>
              <b className="loss">{tally.losses}L</b>
            </span>
            {winRate !== null && <span className="dso-rate">{winRate.toFixed(0)}%</span>}
            <span className={tally.dollars >= 0 ? 'win' : 'loss'}>
              {tally.dollars >= 0 ? '+' : '-'}${Math.abs(tally.dollars).toFixed(0)}
            </span>
          </div>
        )
      ) : (
        <>
      <div className="dso-models">
        {POSITION_MODELS.map((m) => (
          <button
            key={m}
            className={`dso-mode ${model === m ? 'on' : ''}`}
            onClick={() => {
              setModel(m);
              remember(MODEL_KEY, m);
            }}
            title={MODEL_HELP[m]}
          >
            {MODEL_LABEL[m]}
          </button>
        ))}
      </div>

      {opportunities === 0 ? (
        <div className="dso-empty">
          {window_ === 'session' ? 'no diamonds this session' : 'no diamonds in the last 24h'}
        </div>
      ) : (
        <>
          {/* Population first, on its own line. It carries two subtractions now
              and crowding it against the W/L made both hard to read. */}
          <div className="dso-meta">
            <span>{opportunities} signals</span>
            {outcome.skipped > 0 && (
              <span className="dso-skipped" title="Arrived while a position was open">
                −{outcome.skipped} busy
              </span>
            )}
            {/* Never silently dropped: an order that expired unfilled is not a
                loss, but pretending it never existed hides how much of the med
                tier is out of reach. */}
            {outcome.ambiguous > 0 && (
              <span
                className="dso-unfilled"
                title="Bar touched both target and stop — which came first is not knowable from OHLC, so these are excluded from the win rate rather than guessed"
              >
                −{outcome.ambiguous} unclear
              </span>
            )}
            {outcome.unfilled > 0 && (
              <span
                className="dso-unfilled"
                title="Resting limit never touched before it expired — no trade happened"
              >
                −{outcome.unfilled} unfilled
              </span>
            )}
          </div>

          <div className="dso-headline">
            <span className="dso-wl">
              <b className="win">{tally.wins}W</b>
              <b className="loss">{tally.losses}L</b>
              {openCount > 0 && <b className="open">{openCount}?</b>}
            </span>
            {winRate !== null && <span className="dso-rate">{winRate.toFixed(0)}%</span>}
          </div>

          <div className="dso-pnl">
            <span className={tally.points >= 0 ? 'win' : 'loss'}>
              {tally.points >= 0 ? '+' : ''}
              {tally.points.toFixed(0)}pt
            </span>
            <span className={tally.dollars >= 0 ? 'win' : 'loss'}>
              {tally.dollars >= 0 ? '+' : '-'}${Math.abs(tally.dollars).toFixed(0)}
            </span>
          </div>

          {/* Two different risk numbers, side by side because they are
              constantly confused. Drawdown is the fall from a running high;
              low water is how far below zero the curve ever got. A $336
              drawdown off a $308 peak bottoms out at −$28, not −$336. */}
          <div className="dso-risk">
            <span className="dso-dd" title="Worst peak-to-trough fall from a running high">
              dd −${drawdown.toFixed(0)}
            </span>
            <span
              className={`dso-low ${lowWater < 0 ? 'loss' : ''}`}
              title="Lowest the running total ever reached, measured from zero"
            >
              {lowWater < 0 ? `low −$${Math.abs(lowWater).toFixed(0)}` : 'never underwater'}
            </span>
          </div>

          {/* Peak size is the whole risk of doubling and gets its own line, not
              a tooltip. Two consecutive losses is enough to reach 8 contracts. */}
          {reversing && (
            <div className="dso-peak">
              <span>peak size</span>
              <span className={outcome.peakQuantity > 2 ? 'loss' : ''}>
                {outcome.peakQuantity} contracts
              </span>
            </div>
          )}

          {botFloor !== null && botFloor !== undefined && (
            <div className="dso-botfloor">
              bot takes score ≥ {botFloor}
              {/* Only claim a grade is excluded when the floor clears its whole
                  range. At 25 against a weak band of 0-40 it does not. */}
              {thresholds && botFloor >= thresholds.moderateScore && ' · weak excluded'}
              {thresholds && botFloor > 0 && botFloor < thresholds.moderateScore && ' · * partly traded'}
            </div>
          )}

          <div className="dso-grades">
            {byGrade.map(([grade, g]) => {
              const d = g.wins + g.losses;
              const cov = coverage(grade);
              const range = gradeRange(grade);
              return (
                <div
                  className={`dso-grade ${cov === 'none' ? 'not-taken' : ''} ${
                    cov === 'partial' ? 'part-taken' : ''
                  }`}
                  key={grade}
                  title={
                    cov === 'none'
                      ? `Entirely below the bot's ${botFloor} floor — simulated, never traded`
                      : cov === 'partial' && range
                        ? `Straddles the bot's ${botFloor} floor: only ${botFloor}-${range[1]} is traded`
                        : undefined
                  }
                >
                  <span className={`dso-grade-name g-${grade}`}>
                    {grade.replace('-', ' ')}
                    {cov === 'partial' && (
                      <span className="dso-part" title="Partly traded">
                        *
                      </span>
                    )}
                  </span>
                  <span className="dso-wl">
                    <b className="win">{g.wins}</b>
                    <span className="dso-sep">/</span>
                    <b className="loss">{g.losses}</b>
                  </span>
                  <span className="dso-rate">
                    {d > 0 ? `${((100 * g.wins) / d).toFixed(0)}%` : '—'}
                  </span>
                  {/* Second line per group: the score band it covers, and what
                      it actually made. A win rate alone hides which band pays
                      -- at 6 up against 105 down a 90% group can still be the
                      one losing the money. */}
                  <span className="dso-grade-band">
                    {range ? (range[1] === Infinity ? `${range[0]}+` : `${range[0]}-${range[1]}`) : ''}
                  </span>
                  <span className={`dso-grade-pnl ${g.dollars >= 0 ? 'win' : 'loss'}`}>
                    {g.dollars >= 0 ? '+' : '-'}${Math.abs(g.dollars).toFixed(0)}
                    <span className="dso-grade-pts">
                      {' '}
                      {g.points >= 0 ? '+' : ''}
                      {g.points.toFixed(0)}pt
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
};

export default DiamondStatsOverlay;
